import React from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import LoginPage from "@pages/user/login";
import AdminDashboard from "@pages/admin/dashboard";
import AdminLogin from "@pages/admin/login";
import UnauthorizedPage from "@pages/common/UnauthorizedPage";
import NotFoundPage from "@pages/common/NotFoundPage";
import { AuthProvider, Role } from "@context/AuthContext";
import ProtectedRoute from "@components/ProtectedRoute";
import DriversMain from "@pages/user/drivers";
import DriverGroupsMain from "@pages/user/driver_groups";
import UserDashboard from "@pages/user/dashboard";
import WialonLoginRedirect from "@pages/user/wialon-redirect";
import UnitsMain from "@pages/user/units";
import BindingsMain from "@pages/user/bindings";
import { TokenExpiredProvider } from "@context/TokenExpiredProvider";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import ComingSoonPage from "@pages/common/ComingSoonPage";
import UnauthenticatedRoute from "@components/UnauthenticatedRoute";
import UsersMain from "@pages/admin/users";
import WialonLinkAccount from "@pages/user/wialon-redirect/link-account";
import HelpCenter from "@pages/admin/help-center";
import UnitGroupsMain from "@pages/user/unit_groups";

type AppMode = "User" | "Admin";

const App: React.FC = () => {
  const APP_MODE: AppMode =
    (process.env.REACT_APP_APP_MODE as AppMode) || "User";

  const AdminRoutes = () => (
    <Route
      path="admin"
      element={<ProtectedRoute allowedRoles={[Role.Admin]} />}
    >
      <Route path="dashboard" element={<AdminDashboard />} />
      <Route path="users" element={<UsersMain />} />
      <Route path="help-center" element={<HelpCenter />} />
    </Route>
  );

  const UserRoutes = () => (
    <Route path="user" element={<ProtectedRoute allowedRoles={[Role.User]} />}>
      <Route path="dashboard" element={<UserDashboard />} />
      <Route path="drivers-management" element={<DriversMain />} />
      <Route path="driver-groups-management" element={<DriverGroupsMain />} />
      <Route path="units-management" element={<UnitsMain />} />
      <Route path="bindings-management" element={<BindingsMain />} />
      <Route path="unit-groups-management" element={<UnitGroupsMain />} />
    </Route>
  );

  return (
    <AuthProvider>
      <Router>
        <TokenExpiredProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Routes>
              {/* Public Routes */}
              <Route element={<UnauthenticatedRoute />}>
                {APP_MODE === "Admin" ? (
                  <>
                    <Route path="login" element={<AdminLogin />} />
                    <Route path="admin/login" element={<AdminLogin />} />
                  </>
                ) : (
                  <Route path="login" element={<LoginPage />} />
                )}

                {APP_MODE === "User" && (
                  <Route
                    path="link-account/:invitation_code"
                    element={<WialonLinkAccount />}
                  />
                )}
              </Route>

              <Route path="unauthorized" element={<UnauthorizedPage />} />

              {APP_MODE === "User" && (
                <Route
                  path="wialon-redirect"
                  element={<WialonLoginRedirect />}
                />
              )}

              <Route path="help" element={<ComingSoonPage />} />
              <Route path="settings" element={<ComingSoonPage />} />

              {/* Conditionally render routes based on APP_MODE */}
              {APP_MODE === "Admin" ? (
                <>
                  <Route
                    path="/"
                    element={<Navigate to="/admin/dashboard" />}
                  />
                  {AdminRoutes()}
                </>
              ) : (
                <>
                  <Route path="/" element={<Navigate to="/user/dashboard" />} />
                  {UserRoutes()}
                </>
              )}

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </LocalizationProvider>
        </TokenExpiredProvider>
      </Router>
    </AuthProvider>
  );
};

export default App;
